import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useCompStore, usePersistedDataStore } from '../store';
import StableMuiDialog from '../../common/StableMuiDialog.js';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { toast } from '../../common/toast';
import axiosInstance from '../../fetchConfig/axiosConfig';
import CircularProgress from '@mui/material/CircularProgress';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { getParcelAdjustments } from '../../utils/getParcelAdjustments';
import logger from '../../utils/logger';

const AdjustmentsDialog = ({ open, onClose, muniCode, taxYear, currentParcelId = null }) => {
  const storeAdjustments = useCompStore((state) => state.adjustments);
  const availableCountyCodes = storeAdjustments.countycode;
  
  const [selectedParcelId, setSelectedParcelId] = useState(currentParcelId);
  const countyCodes = useMemo(
    () => (Array.isArray(availableCountyCodes) ? availableCountyCodes : (availableCountyCodes ? [availableCountyCodes] : [])),
    [availableCountyCodes]
  );
  const selectedCountyCode = useMemo(
    () => (selectedParcelId ? String(selectedParcelId).slice(0, 5) : ''),
    [selectedParcelId]
  );
  const parcelAdjustments = useMemo(
    () => getParcelAdjustments(selectedParcelId),
    [selectedParcelId, storeAdjustments]
  );

  // Update needs to NOT set the full adjustments obj, just the specific slice.
  // and update the backend update function.
  // extract the adjustments
  const setAdjustments = useCompStore((state) => state.setAdjustments);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Add loading state
  const [isUpdating, setIsUpdating] = useState(false);

  const displayLabels = {
    Acres: 'Land Area (Acres)',
    LotSqFt: 'Lot Area (SqFt)',
    Sqft: 'Living Area (SqFt)',
    Bath: 'Bathrooms (Full)',
    HalfBath: 'Bathrooms (Half)',
    FinishedBasement: 'Finished Basement SqFt',
    BasementSqFtUnfinished: 'Unfinished Basement SqFt',
    FullFinished: 'Full Finished Basement',
    FullPartialFinish: 'Partial Finished Basement',
    FullUnfinished: 'Full Unfinished Basement',
    Bulkhead: 'Bulkhead',
    CentralAir: 'Central Air Conditioning',
    Crawl: 'Crawl Space',
    Deck: 'Deck',
    Patio: 'Patio',
    Porch: 'Porch',
    InGroundPool: 'In-ground Pool',
    PoolAg: 'Above Ground Pool',
    PoolUnsure: 'Unspecified Pool',
    GarageDet: 'Detached Garage',
    TotalGarageCap: 'Garage Capacity',
    Fireplaces: 'Fireplaces',
    Kitchens: 'Kitchens',
    ConditionIn: 'Condition Adj (%)',
    Grade: 'Grade Adj (%)',
  };

  const adjustmentGroups = {
    GeneralFeatures: ['Acres', 'Sqft', 'LotSqFt', 'Bath', 'HalfBath', 'InGroundPool'],
    Basements: ['FinishedBasement', 'BasementSqFtUnfinished', 'FullFinished', 'FullPartialFinish', 'FullUnfinished'],
    Amenities: [
      'Bulkhead',
      'CentralAir',
      'Crawl',
      'Deck',
      'Patio',
      'Porch',
      'PoolAg',
      'PoolUnsure',
      'GarageDet',
      'TotalGarageCap',
      'Fireplaces',
      'Kitchens',
    ],
    Misc: ['ConditionIn', 'Grade'],
  };

  const percentageFields = ['ConditionIn', 'Grade'];

  const metadataFields = ['muniCode', 'taxYear', 'MuniCode', 'TaxYear', 'UserUpdated', 'countycode'];

  // Initialize all fields with 0 if they don't exist
  const initializeAdjustments = (adjustments) => {
    const allFields = [
      ...adjustmentGroups.GeneralFeatures,
      ...adjustmentGroups.Basements,
      ...adjustmentGroups.Amenities,
      ...adjustmentGroups.Misc
    ];
    
    // Create a new object with all fields initialized to 0
    const initialized = {};
    allFields.forEach(field => {
      initialized[field] = 0;
    });
    
    // Then merge in any existing values from adjustments, but exclude metadata fields (this prevents conflicts w/ your update adj function)
    Object.entries(adjustments || {}).forEach(([key, value]) => {
      const normalizedValue = Array.isArray(value) ? value[0] : value;
      if (normalizedValue !== undefined && normalizedValue !== null && !metadataFields.includes(key)) {
        initialized[key] = normalizedValue;
      }
    });
    
    return initialized;
  };

  // Remove useEffect for initializing localAdjustments
  // Use a function to initialize localAdjustments from props.adjustments when dialog is opened
  const [localAdjustments, setLocalAdjustments] = useState(() => initializeAdjustments(parcelAdjustments));
  const hasAdjustments = useMemo(
    () => Boolean(parcelAdjustments && Object.keys(parcelAdjustments).some((key) => !metadataFields.includes(key))),
    [parcelAdjustments, metadataFields]
  );

  useEffect(() => {
    setSelectedParcelId(currentParcelId || null);
  }, [currentParcelId]);
  
  useEffect(() => {
    if (open) {
      setLocalAdjustments(initializeAdjustments(parcelAdjustments));
    }
  }, [open, parcelAdjustments]);

  // stupid but this will work
  useEffect(() => {
    if (!currentParcelId && countyCodes.length > 0 && selectedCountyCode !== String(countyCodes[0])) {
      setSelectedParcelId(String(countyCodes[0]));
    }
  }, [countyCodes, currentParcelId, selectedCountyCode]);


  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeKey, setActiveKey] = useState(null); // Tracks the active key for dropdown

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveKey(null);
  };

  const handleMenuSelect = (option) => {
    if (option === 'IFMV Linked') {
      setLocalAdjustments((prev) => ({
        ...prev,
        [activeKey]: 'IFMV Linked',
      }));
    } else {
      setLocalAdjustments((prev) => ({
        ...prev,
        [activeKey]: 0,
      }));
    }
    handleMenuClose();
  };

  const handleInputChange = (key, value) => {
    // Handle empty or invalid input
    if (value === '' || value === null || value === undefined) {
      setLocalAdjustments((prev) => ({
        ...prev,
        [key]: 0
      }));
      return;
    }

    const numericValue = parseFloat(
      (typeof value === 'string' ? value.replace(/,/g, '') : value) || 0
    );

    // Check if the parsed value is NaN
    if (isNaN(numericValue)) {
      return; // Don't update if the value is NaN
    }

    setLocalAdjustments((prev) => {
      const updated = { ...prev };

      // Handle percentage fields differently
      if (percentageFields.includes(key)) {
        // Store as decimal (e.g., 5% -> 0.05)
        updated[key] = numericValue / 100;
      } else {
        updated[key] = numericValue;
      }

      // Automatically update LotSqFt if Acres changes, converting per-acre to per-square-foot
      if (key === 'Acres') {
        // Convert per-acre value to per-square-foot value (1 acre = 43,560 sq ft)
        updated.LotSqFt = numericValue / 43560;
      }
      if(key === 'Bath'){
        updated.HalfBath = numericValue / 2;
      }

      return updated;
    });
  };

  // Add a new function to handle display value
  const getDisplayValue = (key, value) => {
    // If the value is NaN, return empty string
    if (isNaN(value)) return '';
    
    // For percentage fields
    if (percentageFields.includes(key)) {
      return (value * 100).toLocaleString();
    }
    
    // For all other fields, including zero values
    return value.toLocaleString();
  };

  const updateAdjustmentsForCounty = () => {
    const countyIndex = countyCodes.findIndex((code) => String(code) === selectedCountyCode);
    const updatedAdjustments = { ...storeAdjustments };

    Object.entries(localAdjustments).forEach(([key, value]) => {
      if (key === 'countycode' || key === 'muniCode' || key === 'taxYear') return;

      const existingValue = updatedAdjustments[key];
      if (Array.isArray(existingValue)) {
        const next = [...existingValue];
        next[countyIndex] = value;
        updatedAdjustments[key] = next;
        return;
      }

      if (countyCodes.length > 0) {
        updatedAdjustments[key] = countyCodes.map((_, index) => (
          index === countyIndex ? value : (existingValue ?? 0)
        ));
        return;
      }

      updatedAdjustments[key] = value;
    });

    setAdjustments(updatedAdjustments);
  };

  // saving function to update frontend adjustments and see how things change
  const handleSave = () => {
    toast.info('Displayed adjustments updated. To actually save these adjustments you must use the update database button.');
    updateAdjustmentsForCounty();
    onClose();
  };

  // open the db update confirmation, then kick off handleConfirmUpdate
  const handleDBSave = () => {
    setConfirmDialogOpen(true); // Open the confirmation dialog
  };

    const handleConfirmUpdate = async () => {
    setIsUpdating(true); // Start loading
    
    // Use muniCode prop instead of getNegotiationObj.MuniCode
    if (!muniCode) {
      toast.error('No municipality data available. Please refresh the page and try again.');
      setIsUpdating(false);
      setConfirmDialogOpen(false);
      return;
    }
    
    const districtCode = muniCode.slice(1);
  
    // Key transformation mapping for the backend
    const keyMapping = {
      Bath: [['BathsFull', 'BathsHalf'], (value) => [value, value / 2]], // Derive both BathsFull and BathsHalf
      Acres: ['LotSqFt', (value) => value / 43560], // Convert Acres to LotSqFt
      Sqft: ['LivingArea', (value) => value],
    };
  
    const handleSpecialCases = (key, value, backendAdjustments) => {
      if (key === 'TotalGarageCap') {
        const garageDet = value; // Example split logic
        const garageAtt = value;
        backendAdjustments['GarageDet'] = garageDet;
        backendAdjustments['GarageAtt'] = garageAtt;
      }
    };
  
    const prepareAdjustmentsForBackend = () => {
      const { countycode, ...remainingAdjustments } = localAdjustments;
      logger.log('remainingAdjustments', remainingAdjustments);
      const currentCountyCode = selectedCountyCode.slice(0, 5);
  
      const backendAdjustments = {
        District: districtCode, // Add District
      };
  
      // Process adjustments with mappings and special cases
      Object.entries(remainingAdjustments).forEach(([key, value]) => {
        if (keyMapping[key]) {
          const [mappedKeys, transformFn] = keyMapping[key];
  
          // Check if mapping generates multiple backend keys
          if (Array.isArray(mappedKeys)) {
            const transformedValues = transformFn(value);
            mappedKeys.forEach((mappedKey, index) => {
              backendAdjustments[mappedKey] = transformedValues[index];
            });
          } else {
            backendAdjustments[mappedKeys] = transformFn(value);
          }
        } else if (key === 'TotalGarageCap') {
          handleSpecialCases(key, value, backendAdjustments);
        } else {
          backendAdjustments[key] = value; // Add keys without transformation
        }
      });

      logger.log('post key mapping backendAdjustments', backendAdjustments);
  
      // Explicitly set the countycode at the end to avoid overwrites
      backendAdjustments.countycode = currentCountyCode
  
      return backendAdjustments;
    };
  
    const adjustmentsForBackend = prepareAdjustmentsForBackend();
    // const adjustmentsForBackend = localAdjustments;
    delete adjustmentsForBackend.HalfBath
    
    // Remove any metadata fields that shouldn't be treated as database columns
    delete adjustmentsForBackend.muniCode;
    delete adjustmentsForBackend.MuniCode;
    
    // return
  
    try {
      const adjustmentUpdateObject = {
        adjustments: adjustmentsForBackend,
      }
      logger.log('adjustments being sent in to update backend', adjustmentUpdateObject);

      // rename ajdustments data to match our backend (stupid but whatever).
      // this shouldn't 
      // adjustmentsForBackend.LivingArea = adjustmentsForBackend.GLA;
      // adjustmentsForBackend.LotSquareFeet = adjustmentsForBackend.LotSqFt;
      // delete adjustmentsForBackend.GLA;
      delete adjustmentsForBackend.District; // unnecessary once the loaded data moves to new table.

      const response = await axiosInstance.post('/update_adjustments', adjustmentUpdateObject);

      if (response.status === 200) {
        // Save adjustments locally as well, with muniCode and taxYear metadata
        logger.log('[AdjustmentsDialog] handleConfirmUpdate - DB update success, saving to store.');
        updateAdjustmentsForCounty();
        toast.success(`Adjustments updated, and saved in the database for ${localAdjustments.countycode} successfully!`);
      } else {
        toast.error(`Failed to update adjustments: ${response.statusText}`);
      }
    } catch (error) {
      logger.error('Error updating adjustments:', error);
    } finally {
      setIsUpdating(false); // Stop loading
      setConfirmDialogOpen(false);
      onClose();
    }
  };
  
  
  

  
  const handleCancelUpdate = () => {
    setConfirmDialogOpen(false); // Close the confirmation dialog
  };

  return (
    <div className='justify-between h-full z-999'>
      {countyCodes.length > 0 && (
        <Tabs
          value={selectedCountyCode || false}
          onChange={(event, value) => setSelectedParcelId(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          {countyCodes.map((code) => (
            <Tab key={String(code)} value={String(code)} label={String(code)} />
          ))}
        </Tabs>
      )}
      {!hasAdjustments ? (
        <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column" gap={1.5} minHeight="240px">
          <CircularProgress size={22} />
          <Typography>Loading Adjustments</Typography>
        </Box>
      ) : (
        <>
          {/* Generic input, and custom handling for SQFT input */}
          <Box display="flex" gap={4}>
            {['GeneralFeatures', 'Basements'].map((group) => (
              <Box key={group} flex={1}>
                <Typography variant="h6" gutterBottom>
                  {group === 'GeneralFeatures' ? 'General Features' : 'Basements'}
                </Typography>
                <Grid container spacing={2}>
                  {adjustmentGroups[group]
                    .filter((key) => Object.keys(localAdjustments).includes(key))
                    .map((key) => (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        {key === 'Sqft' ? (
                          <>
                          {/* SQFT field */}
                            <TextField
                              fullWidth
                              label="Living Area (SqFt)"
                              value={
                                localAdjustments[key] === 'IFMV Linked'
                                  ? 'IFMV Linked'
                                  : localAdjustments[key].toLocaleString()
                              }
                              onChange={(e) => {
                                if (localAdjustments[key] !== 'IFMV Linked') {
                                  handleInputChange(
                                    key,
                                    parseFloat(e.target.value.replace(/,/g, '')) || 0
                                  );
                                }
                              }}
                              InputProps={{
                                startAdornment:
                                  localAdjustments[key] !== 'IFMV Linked' ? (
                                    <InputAdornment position="start">$</InputAdornment>
                                  ) : null,
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={(e) => {
                                        // Anchor to the TextField itself
                                        setMenuAnchor(e.currentTarget.closest('.MuiFormControl-root'));
                                        setActiveKey(key);
                                      }}
                                      size="small"
                                      aria-label="open menu"
                                    >
                                      <ArrowDropDownIcon />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                              variant="outlined"
                              disabled={localAdjustments[key] === 'IFMV Linked'}
                              sx={
                                localAdjustments[key] === 'IFMV Linked'
                                  ? { backgroundColor: '#f5f5f5' }
                                  : {}
                              }
                            />
                            <Menu
                              anchorEl={menuAnchor}
                              open={Boolean(menuAnchor) && activeKey === key}
                              onClose={handleMenuClose}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left", // Align with the left edge of the TextField
                              }}
                              transformOrigin={{
                                vertical: "top",
                                horizontal: "left", // Align with the left edge of the TextField
                              }}
                              PaperProps={{
                                style: {
                                  width: menuAnchor?.offsetWidth || "auto", // Match TextField width
                                },
                              }}
                            >
                              <MenuItem onClick={() => handleMenuSelect('IFMV Linked')}>
                                IFMV Linked
                              </MenuItem>
                              <MenuItem onClick={() => handleMenuSelect('Custom Value')}>
                                Custom Value
                              </MenuItem>
                            </Menu>
                          </>
                        ) : (
                          <TextField
                            fullWidth
                            label={key}
                            disabled={key === 'LotSqFt' || key === 'HalfBath'}
                            value={localAdjustments[key].toLocaleString()}
                            onChange={(e) => handleInputChange(key, e.target.value)}
                            variant="outlined"
                          />
                        )}
                      </Grid>
                    ))}
                </Grid>
              </Box>
            ))}
          </Box>

          {/* Amenity section */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Amenities
            </Typography>
            <Grid container spacing={2}>
              {adjustmentGroups.Amenities.filter((key) =>
                Object.keys(localAdjustments).includes(key)
              ).map((key) => (
                <Grid item xs={12} sm={4} md={2} key={key}>
                  <TextField
                    fullWidth
                    label={displayLabels[key] || key}
                    value={getDisplayValue(key, localAdjustments[key] || 0)}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                    inputProps={{ maxLength: 10 }}
                    variant="outlined"
                    InputProps={{
                      startAdornment: !percentageFields.includes(key) ? (
                        <InputAdornment position="start">$</InputAdornment>
                      ) : null,
                      endAdornment: percentageFields.includes(key) ? (
                        <InputAdornment position="end">%</InputAdornment>
                      ) : null,
                    }}
                    sx={{
                      maxWidth: percentageFields.includes(key) ? '120px' : '100%',
                      width: percentageFields.includes(key) ? '100%' : 'auto',
                      marginRight: '8px',
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Misc section */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Misc
            </Typography>
            <Grid container spacing={2}>
              {adjustmentGroups.Misc.filter((key) =>
                Object.keys(localAdjustments).includes(key)
              ).map((key) => (
                <Grid item xs={12} sm={4} md={3} key={key}>
                  <TextField
                    fullWidth
                    label={displayLabels[key] || key}
                    value={getDisplayValue(key, localAdjustments[key] || 0)}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                    inputProps={{ maxLength: 10 }}
                    variant="outlined"
                    InputProps={{
                      startAdornment: !percentageFields.includes(key) ? (
                        <InputAdornment position="start">$</InputAdornment>
                      ) : null,
                      endAdornment: percentageFields.includes(key) ? (
                        <InputAdornment position="end">%</InputAdornment>
                      ) : null,
                    }}
                    sx={{
                      maxWidth: percentageFields.includes(key) ? '120px' : '100%',
                      width: percentageFields.includes(key) ? '100%' : 'auto',
                      marginRight: '8px',
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </>
      )}

      <div className="absolute right-2 bottom-2 justify-end">
      <Button onClick={handleDBSave} disabled={!hasAdjustments} color="error" variant="outlined" className='mr-2'
          sx={{
            '&.Mui-disabled': {
              color: 'gray !important',
              backgroundColor: 'transparent !important',
              borderColor: 'gray',
            },
          }}
          >
        Update DB
      </Button>

      <Button onClick={onClose} color="primary" className='mr-2'>
        Close
      </Button>
      <Button onClick={handleSave} disabled={!hasAdjustments} color="primary" variant="contained">
        Save
      </Button>
      </div>

      {/* Confirmation Dialog */}
      <StableMuiDialog
        open={confirmDialogOpen}
        onClose={handleCancelUpdate}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          <Typography>
            This will overwrite the database's adjustments values <strong>PERMANENTLY</strong> for {muniCode}. Confirm this is intentional.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUpdate} color="primary" disabled={isUpdating}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmUpdate} 
            color="error" 
            variant="contained"
            disabled={isUpdating}
            sx={{
              opacity: isUpdating ? 0.7 : 1,
              minWidth: '80px', // Prevent button size changes
            }}
          >
            {isUpdating ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Updating...
                <CircularProgress size={12} color="inherit" />
              </Box>
            ) : (
              'Update'
            )}
          </Button>
        </DialogActions>
      </StableMuiDialog>
    </div>
  );
};

export default AdjustmentsDialog;